import React, { useMemo } from 'react';
import styles from './index.module.scss';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import ReasonForChangeDialog from '@components-clean/core/dialogs/reason-for-change-dialog';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { SubjectReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-review-instance';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type Props = {
    canEdit: boolean;
    studyRef: string;
    datasetId: number;
    subjectId: number;
    subjectNumber: string;
    reviewRecordId: number;
    sessionId: string;
};

const SubjectProfileDetailsReviewCard = ({
    canEdit,
    studyRef,
    datasetId,
    subjectId,
    subjectNumber,
    reviewRecordId,
    sessionId,
}: Props) => {
    const { t } = useTranslation('sm', {
        keyPrefix: 'subjectProfile.activeReviewCard',
    });
    const { t: tCommon } = useTranslation('common');

    const getReviewQuery = SubjectMonitoringReviewAPI.useGetByRecordIdQuery(studyRef, reviewRecordId);
    const getReviewConfigQuery = SubjectMonitoringReviewAPI.useGetConfigForReview(studyRef, reviewRecordId);
    const getAllSubjectReviewInstancesQuery = SubjectReviewInstanceApi.useGetSubjects(studyRef, reviewRecordId);

    const addSubjectReviewInstanceMutation = SubjectReviewInstanceApi.usePostReview(studyRef);
    const removeSubjectReviewInstancesMutation = SubjectReviewInstanceApi.useRemoveFromReview(studyRef);
    const setSubjectReviewInstanceReviewedMutation =
        SubjectReviewInstanceApi.useSetSubjectReviewInstanceReviewedMutation(studyRef);

    // Look up the review instance for the current subject, if one exists
    const subjectReviewInstance = useMemo(() => {
        return (getAllSubjectReviewInstancesQuery.data ?? []).find((s) => s.subjectId === subjectId);
    }, [getAllSubjectReviewInstancesQuery.data, subjectId]);

    const isLoading = getReviewQuery.isLoading || getAllSubjectReviewInstancesQuery.isLoading;
    const isError = getReviewQuery.error != null || getAllSubjectReviewInstancesQuery.error != null;
    const isMutating =
        addSubjectReviewInstanceMutation.isMutating ||
        removeSubjectReviewInstancesMutation.isMutating ||
        setSubjectReviewInstanceReviewedMutation.isMutating;

    const showReasonForRemoveDialog = useBoolean(false);

    const review = getReviewQuery.data;
    const reviewConfig = getReviewConfigQuery.data;

    const addToReview = async () => {
        if (review?.id == null) {
            return;
        }

        let success: boolean;

        try {
            success = await addSubjectReviewInstanceMutation.trigger({
                subjects: {
                    [subjectId]: subjectNumber,
                },
                datasetId,
                reviewId: review.id,
                reviewModeSessionId: sessionId,
            });
        } catch {
            success = false;
        }

        if (success) {
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } else {
            ShowError(tCommon('errorMessage'));
        }
    };

    const removeFromReview = async (reason: string) => {
        if (review?.recordId == null) {
            return;
        }

        let success: boolean;

        try {
            success = await removeSubjectReviewInstancesMutation.trigger({
                datasetId,
                reviewRecordId: review.recordId,
                subjectIds: [subjectId],
                reason,
                sessionId,
            });
        } catch {
            success = false;
        }

        if (success) {
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } else {
            ShowError(tCommon('errorMessage'));
        }
    };

    const markAsReviewed = async () => {
        if (review?.recordId == null) {
            return;
        }

        let success: boolean;

        try {
            success = await setSubjectReviewInstanceReviewedMutation.trigger({
                datasetId,
                reviewRecordId: review.recordId,
                subjectId,
                setToReviewed: true,
                sessionId,
            });
        } catch {
            success = false;
        }

        if (success) {
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } else {
            ShowError(tCommon('errorMessage'));
        }
    };

    const unmarkAsReviewed = async () => {
        if (review?.recordId == null) {
            return;
        }

        let success: boolean;

        try {
            success = await setSubjectReviewInstanceReviewedMutation.trigger({
                datasetId,
                reviewRecordId: review.recordId,
                subjectId,
                setToReviewed: false,
                sessionId,
            });
        } catch {
            success = false;
        }

        if (success) {
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } else {
            ShowError(tCommon('errorMessage'));
        }
    };

    let footerActions: React.ReactNode | undefined;
    // Only include actions if all queries have loaded successfully
    if (canEdit && !isLoading && !isError) {
        const removeFromReviewButton = (
            <Button
                data-testid="remove-from-review-button"
                primary
                alternativeStyle
                onClick={showReasonForRemoveDialog.setTrue}
                disabled={isMutating}
            >
                {t('actions.remove')}
            </Button>
        );

        if (subjectReviewInstance == null) {
            // Not on review actions
            footerActions = (
                <div className={styles.footerActions}>
                    <Button data-testid="add-to-review-button" primary onClick={addToReview} disabled={isMutating}>
                        {t('actions.addToReview')}
                    </Button>
                </div>
            );
        } else if (subjectReviewInstance.reviewed) {
            // On review and reviewed actions
            footerActions = (
                <div className={styles.footerActions}>
                    {removeFromReviewButton}
                    <Button
                        data-testid="unmark-as-reviewed-button"
                        primary
                        onClick={unmarkAsReviewed}
                        disabled={isMutating}
                    >
                        {t('actions.unmarkAsReviewed')}
                    </Button>
                </div>
            );
        } else {
            // On review and not reviewed actions
            footerActions = (
                <div className={styles.footerActions}>
                    {removeFromReviewButton}
                    <Button
                        data-testid="unmark-as-reviewed-button"
                        primary
                        onClick={markAsReviewed}
                        disabled={isMutating}
                    >
                        {t('actions.markAsReviewed')}
                    </Button>
                </div>
            );
        }
    }

    if (reviewConfig?.reviewSubject !== true) {
        return null;
    }

    return (
        <>
            <Card
                data-testid="subject-profile-details-tags-card"
                title={!isLoading && !isError ? review?.title : undefined}
                footerActions={footerActions}
            >
                {isLoading || isError ? (
                    <div className={styles.cardLoadingStateWrapper}>
                        {isError ? (
                            <InfoField type="Error" message={tCommon('errorMessage')} />
                        ) : isLoading ? (
                            <Loader />
                        ) : null}
                    </div>
                ) : (
                    <>
                        <p className={styles.reviewTypeAndDataset}>
                            {review?.type} - {review?.datasetDate}
                        </p>

                        <div className={styles.infoBoxWrapper}>
                            {subjectReviewInstance == null ? (
                                <InfoField type="Info" message={t('status.subjectNotOnReview')} />
                            ) : subjectReviewInstance.reviewed ? (
                                <InfoField type="Info" message={t('status.reviewComplete')} />
                            ) : (
                                <InfoField type="Info" message={t('status.pendingReview')} />
                            )}
                        </div>
                    </>
                )}
            </Card>

            {showReasonForRemoveDialog.value && (
                <ReasonForChangeDialog
                    maxLength={3000}
                    onSubmit={async (reason) => {
                        await removeFromReview(reason);
                        showReasonForRemoveDialog.setFalse();
                    }}
                    onCancel={showReasonForRemoveDialog.setFalse}
                />
            )}
        </>
    );
};

export default SubjectProfileDetailsReviewCard;
